import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, ButtonBase, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import { ClientTasksResult } from '../../dashboard/tasks/useClientTasks';
import { buildWorkload, CoachWorkload, heatLevel, plural, WORKLOAD_COLUMNS, WorkloadColumn } from '../../dashboard/tasks/workload';
import { INK, panelSx, SourceNotice } from '../../dashboard/ui';
import { WorkloadDrawer } from './WorkloadDrawer';

/** One warm ramp, light to dark, with text that passes contrast on each step. */
const HEAT = [
  { bg: 'transparent', color: '#94A3B8' },
  { bg: '#FEF6E7', color: '#5C3A00' },
  { bg: '#FBDDAE', color: '#4A2600' },
  { bg: '#F2A65A', color: '#3A1C00' },
  { bg: '#B4471A', color: '#FFFFFF' },
];
const GRID = { display: 'grid', gridTemplateColumns: `minmax(200px, 1.7fr) repeat(${WORKLOAD_COLUMNS.length + 1}, minmax(76px, 1fr))`, alignItems: 'center' } as const;

/**
 * Managers' and admins' view of where open work sits: one row per lead coach,
 * every number a count of clients (a client with three assessments due counts once),
 * one column per kind of task, coloured by how much there is. Any number opens
 * the clients behind it.
 */
export const WorkloadView: React.FC<{ tasks: ClientTasksResult; isAdmin: boolean }> = ({ tasks, isAdmin }) => {
  const [location, setLocation] = useState<string>('all');
  // Kept as ids so a refresh re-reads the open drawer's numbers instead of showing a stale copy.
  const [drill, setDrill] = useState<{ coachId: string | null; column: WorkloadColumn } | null>(null);
  const groups = useMemo(() => buildWorkload(tasks.clients, tasks.work), [tasks.clients, tasks.work]);
  const shown = location === 'all' ? groups : groups.filter((g) => g.location === location);
  const coaches = shown.flatMap((g) => g.coaches);
  const max = Math.max(0, ...coaches.flatMap((c) => WORKLOAD_COLUMNS.map(({ key }) => c.counts[key])));
  const open = coaches.reduce((sum, c) => sum + c.open, 0);
  const openTasks = coaches.reduce((sum, c) => sum + c.openTasks, 0);
  const drillCoach: CoachWorkload | null = drill ? coaches.find((c) => c.coachId === drill.coachId) ?? null : null;
  const mostOverdue = [...coaches].filter((c) => c.coachId).sort((a, b) => b.counts.overdue - a.counts.overdue)[0];

  if (tasks.status === 'idle') {
    return <Alert severity="info">Your account has no location assigned, so there is no team workload to show.</Alert>;
  }
  if (tasks.status === 'error') {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={tasks.refresh}>Retry</Button>}>
        {tasks.error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
        <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
          <Typography component="h1" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 600, letterSpacing: '-0.02em', mb: 0.5 }}>
            Team workload
          </Typography>
          <Typography sx={{ fontSize: 15, color: INK.body }}>
            {tasks.status !== 'ready'
              ? 'Loading clients…'
              : `${plural(open, ['client has', 'clients have'])} open work (${plural(openTasks, ['task', 'tasks'])}) across ${plural(coaches.filter((c) => c.coachId).length, ['coach', 'coaches'])}.` +
                (mostOverdue && mostOverdue.counts.overdue > 0
                  ? ` ${mostOverdue.coachName} has the most clients overdue (${mostOverdue.counts.overdue}).`
                  : '')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {isAdmin && groups.length > 1 ? (
            <Box role="group" aria-label="Location" sx={{ display: 'flex', p: 0.5, gap: 0.5, bgcolor: '#E7EBF0', borderRadius: '10px', flexWrap: 'wrap' }}>
              {['all', ...groups.map((g) => g.location)].map((option) => {
                const on = option === location;
                return (
                  <ButtonBase
                    key={option}
                    aria-pressed={on}
                    onClick={() => setLocation(option)}
                    sx={{ height: 34, px: 1.5, borderRadius: '7px', fontSize: 14, fontWeight: 500, bgcolor: on ? '#FFFFFF' : 'transparent', boxShadow: on ? '0 1px 2px rgba(15,23,42,0.12)' : 'none' }}
                  >
                    {option === 'all' ? 'All locations' : option}
                  </ButtonBase>
                );
              })}
            </Box>
          ) : null}
          <Typography component="span" sx={{ fontSize: 13, color: INK.muted }}>
            {tasks.refreshing ? 'Updating…' : tasks.error ? tasks.error : tasks.loadedAt ? `Updated ${format(tasks.loadedAt, 'HH:mm')}` : ''}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton aria-label="Refresh" size="small" onClick={tasks.refresh}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <SourceNotice sources={tasks.sources} failures={tasks.failures} onRetry={tasks.refresh} />
      </Box>

      <Box sx={{ ...panelSx, overflowX: 'auto' }}>
        <Box role="table" aria-label="Open tasks by coach and kind" sx={{ minWidth: 980 }}>
          <Box role="row" sx={{ ...GRID, alignItems: 'end', px: 1.5, py: 1.25, borderBottom: `1px solid ${INK.hairline}`, fontSize: 12, fontWeight: 600, color: INK.muted }}>
            <Box role="columnheader" sx={{ px: 0.75 }}>Coach</Box>
            {WORKLOAD_COLUMNS.map((c) => (
              <Box role="columnheader" key={c.key} sx={{ px: 0.75, textAlign: 'center' }}>{c.label}</Box>
            ))}
            <Box role="columnheader" sx={{ px: 0.75, textAlign: 'right' }}>Clients with work</Box>
          </Box>

          {tasks.status !== 'ready'
            ? [0, 1, 2, 3].map((i) => (
                <Box role="row" key={i} sx={{ px: 2 }}>
                  <Box role="cell">
                    <Skeleton height={48} />
                  </Box>
                </Box>
              ))
            : shown.map((group) => (
                <Box role="rowgroup" key={group.location}>
                  <Box role="row" sx={{ ...GRID, px: 1.5, py: 1, bgcolor: '#F8FAFC', borderBottom: `1px solid ${INK.faint}`, fontSize: 12, fontWeight: 600, color: INK.body }}>
                    <Box role="rowheader" sx={{ px: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {group.location} · {group.coaches.length}
                    </Box>
                    {WORKLOAD_COLUMNS.map((c) => (
                      <Box role="cell" key={c.key} sx={{ textAlign: 'center' }}>{group.totals[c.key]}</Box>
                    ))}
                    <Box role="cell" sx={{ px: 0.75, textAlign: 'right' }}>{group.totals.open}</Box>
                  </Box>
                  {group.coaches.map((coach) => (
                    <Box role="row" key={coach.coachId ?? 'none'} sx={{ ...GRID, px: 1.5, py: 0.75, borderBottom: `1px solid ${INK.faint}` }}>
                      <Box role="rowheader" sx={{ px: 0.75, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{coach.coachName}</Typography>
                        <Typography sx={{ fontSize: 12, color: INK.muted }}>{coach.activeClients} active clients</Typography>
                      </Box>
                      {WORKLOAD_COLUMNS.map(({ key, label }) => {
                        const n = coach.counts[key];
                        const heat = HEAT[heatLevel(n, max)];
                        const on = drill?.coachId === coach.coachId && drill?.column === key;
                        return (
                          <Box role="cell" key={key} sx={{ p: 0.375 }}>
                            <ButtonBase
                              aria-label={`${coach.coachName}, ${label}: ${plural(n, ['client', 'clients'])}`}
                              disabled={n === 0}
                              onClick={() => setDrill({ coachId: coach.coachId, column: key })}
                              sx={{ width: '100%', height: 40, borderRadius: '8px', bgcolor: heat.bg, color: heat.color, fontWeight: n ? 600 : 400, fontSize: 14, boxShadow: on ? `inset 0 0 0 2px ${INK.main}` : 'none' }}
                            >
                              {n || '·'}
                            </ButtonBase>
                          </Box>
                        );
                      })}
                      <Box role="cell" sx={{ px: 0.75, textAlign: 'right', fontWeight: 600, fontSize: 15 }}>{coach.open}</Box>
                    </Box>
                  ))}
                </Box>
              ))}
        </Box>
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: 12, color: INK.muted }}>
        Numbers are clients: a client with several tasks of one kind counts once, and the drill-down lists the tasks. Darker means more. Overdue counts clients with a milestone past its deadline; success updates have their own column. Renewals are Ignition, Foundation and Pre/Post Natal programmes ending within 14 days.
      </Typography>

      <WorkloadDrawer coach={drillCoach} column={drill?.column ?? null} onClose={() => setDrill(null)} />
    </Box>
  );
};
